<script setup>
import { ref, computed } from 'vue';
import { useReportMetrics } from 'dashboard/composables/useReportMetrics';
import ChartConversationViewModal from './ChartConversationViewModal.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { STATUS } from 'dashboard/store/constants';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  metric: {
    type: Object,
    default: () => ({}),
  },
  accountSummaryKey: {
    type: String,
    default: 'getAccountSummary',
  },
  conversationDataFilterOptions: {
    type: Object,
    default: () => ({}),
  },
  summaryFetchingKey: {
    type: String,
    default: 'getAccountSummaryFetchingStatus',
  },
});
const showConversationViewModal = ref(false);
const ALLOWED_METRIC_KEYS_FOR_CONVERSATION_VIEW = ['conversations_count'];

const { t } = useI18n();

const { calculateTrend, displayMetric, isAverageMetricType, fetchingStatus } =
  useReportMetrics(props.accountSummaryKey, props.summaryFetchingKey);

const trendColor = (value, key) => {
  if (isAverageMetricType(key)) {
    return value > 0
      ? 'border-n-ruby-9 text-n-ruby-9'
      : 'border-n-teal-10 text-n-teal-10';
  }
  return value < 0
    ? 'border-n-ruby-9 text-n-ruby-9'
    : 'border-n-teal-10 text-n-teal-10';
};

const toogleShowConversationViewModal = () => {
  showConversationViewModal.value = !showConversationViewModal.value;
};

const closeConversationViewModal = () => {
  showConversationViewModal.value = false;
};

const shouldShowConversationViewButton = computed(() => {
  return (
    ALLOWED_METRIC_KEYS_FOR_CONVERSATION_VIEW.includes(props.metric.KEY) &&
    Number(displayMetric(props.metric.KEY))
  );
});
</script>

<template>
  <div class="text-n-slate-11">
    <span class="text-sm">
      {{ metric.NAME }}
    </span>
    <div class="flex items-end text-n-slate-12">
      <div v-if="fetchingStatus === STATUS.FETCHING">
        <Spinner />
      </div>
      <div
        v-else-if="fetchingStatus === STATUS.FAILED"
        class="text-n-ruby-10 text-sm"
      >
        {{ t('REPORT.SUMMARY_FETCHING_FAILED') }}
      </div>
      <div
        v-else-if="fetchingStatus === STATUS.FINISHED"
        class="text-xl font-medium"
      >
        {{ displayMetric(metric.KEY) }}
      </div>
      <div
        v-if="metric.trend && fetchingStatus === STATUS.FINISHED"
        class="text-xs ml-4 flex items-center mb-0.5"
      >
        <div
          v-if="metric.trend < 0"
          class="h-0 w-0 border-x-4 medium border-x-transparent border-t-[8px] mr-1"
          :class="trendColor(metric.trend, metric.KEY)"
        />
        <div
          v-else
          class="h-0 w-0 border-x-4 medium border-x-transparent border-b-[8px] mr-1"
          :class="trendColor(metric.trend, metric.KEY)"
        />
        <span class="font-medium" :class="trendColor(metric.trend, metric.KEY)">
          {{ calculateTrend(metric.KEY) }}%
        </span>
      </div>
    </div>
    <div
      v-if="shouldShowConversationViewButton"
      class="text-sm text-gray-500 dark:text-gray-400"
    >
      <woot-button
        color-scheme="info"
        icon="chat"
        class="clear"
        @click="toogleShowConversationViewModal"
      >
        {{ $t('REPORT.CONVERSATIONS_VIEW.OPEN_BUTTON') }}
      </woot-button>
    </div>
    <woot-modal
      v-if="showConversationViewModal"
      v-model:show="showConversationViewModal"
      :on-close="closeConversationViewModal"
      size="medium"
    >
      <ChartConversationViewModal
        :metric="metric"
        :conversation-data-filter-options="conversationDataFilterOptions"
        :total-response-count="Number(displayMetric(metric.KEY))"
        @close-conversation-view-modal="closeConversationViewModal"
      />
    </woot-modal>
  </div>
</template>
